import { InfiniteTable } from '@/components/_common';
import EyeIcon from '@/components/_common/EyeIcon';
import {
  useBankSlips,
  useRetrieveBankSlip,
} from '@/core/domain/interno/Omie/Omie.hooks';
import {
  BankSlip,
  ListBankSlipQuery,
} from '@/core/domain/interno/Omie/Omie.types';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Space, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import BankSlipsListFilters from './BankSlipsListFilters';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { useDebounce, useReportGenerate } from '@/hooks';
import { useState } from 'react';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';

export default function BankSlipList() {
  const [queryParams, setQueryParams] = useState<ListBankSlipQuery>({
    order: 'ASC',
    ...convertParams<ListBankSlipQuery>({
      search: 'string',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);

  usePersistSearchParams(queryDebounce);

  const retrieveBankSlip = useRetrieveBankSlip();
  const report = useReportGenerate();
  const { data, fetchNextPage, hasNextPage, isFetching } =
    useBankSlips(queryDebounce);

  const bankSlips = flatInfiniteData(data);

  const columns: ColumnsType<BankSlip> = [
    {
      title: 'Empresa',
      dataIndex: 'companyName',
      key: 'companyName',
      ellipsis: true,
      width: 240,
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
      ellipsis: true,
      width: 100,
      render: (value) => applyMask(value, cnpjMask),
    },
    {
      title: 'Data de emissão',
      dataIndex: 'bankSlipEmissionDate',
      key: 'bankSlipEmissionDate',
      ellipsis: true,
      width: 100,
    },
    {
      title: 'Código de barras',
      dataIndex: 'bankSlipBarcode',
      key: 'bankSlipBarcode',
      ellipsis: true,
      width: 300,
      render: (value) => (
        <EllipsisHoverToggle>
          <Typography.Paragraph copyable style={{ margin: 0 }}>
            {value}
          </Typography.Paragraph>
        </EllipsisHoverToggle>
      ),
    },
    {
      title: 'Valor',
      dataIndex: 'documentValue',
      key: 'documentValue',
      ellipsis: true,
      width: 100,
      render: (value) => `R$ ${quantityFormatter(value)}`,
    },
    {
      title: 'Ações',
      width: 50,
      align: 'right',
      fixed: 'right',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <EyeIcon
            loading={retrieveBankSlip.isLoading}
            iconProps={{
              onClick: async () => {
                const bankSlip = await retrieveBankSlip.mutateAsync(
                  row.companyId,
                );

                if (!!bankSlip) {
                  report.openFile({
                    result: bankSlip,
                    fileName: 'Boleto_bancário.pdf',
                    fileType: 'application/pdf',
                  });
                }
              },
            }}
            content="Visualizar Boleto"
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <BankSlipsListFilters
        queryParams={queryParams}
        setQueryParams={setQueryParams}
      />

      <InfiniteTable<BankSlip>
        columns={columns}
        loading={isFetching}
        dataSource={bankSlips}
        rowKey={(row) => row.companyId}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
      />
    </>
  );
}
